import { useCallback, useEffect, useState } from 'react';

import { getItemCardActions } from '../services/cardActions';
import { normalizeId } from '../utils/ids';
import { useSessionUserId } from '../utils/session';

/**
 * Whether each of a record's card actions applies to THIS record (KPR-1342):
 * the API's one-record read (GET /api/items/{id}/card-actions), which judges
 * every action's condition as the caller reads the record — the same judge a
 * press asks. The client never matches a condition itself.
 *
 * Asked only when it is worth asking (`enabled`: the card has a conditioned
 * action, and the surface wants the answer now — the item form and the detail
 * pane at once, a row or a tile when its ⋯ opens), and again whenever
 * `version` changes (the record was saved, refreshed or pressed) or `reload()`
 * is called (a press the API refused as not applicable). While the same record
 * is read again its last answer stays, so a header does not flicker; another
 * record starts from nothing.
 *
 * Returns `{ answers, failed, reload }`: `answers` a Map of action id to
 * `{ applies, reason }`, or null until the first answer; `failed` when the
 * last read did not land (the menu then offers every action, and the press
 * asks the server).
 */
export default function useRecordCardActions(itemId, { enabled = true, version = '' } = {}) {
	const id = enabled ? normalizeId(itemId) : '';
	const who = useSessionUserId();
	const [nonce, setNonce] = useState(0);
	const [state, setState] = useState({ owner: '', key: '', answers: null, failed: false });
	const owner = id ? `${who || ''}:${id}` : '';
	const key = owner ? `${owner}:${version}:${nonce}` : '';

	useEffect(() => {
		if (!key) return undefined;
		let active = true;
		getItemCardActions(id)
			.then((rows) => {
				if (!active) return;
				const answers = new Map(rows.map(row => [normalizeId(row._id), { applies: row.applies !== false, reason: row.reason || '' }]));
				setState({ owner, key, answers, failed: false });
			})
			.catch(() => {
				if (active) setState(prev => ({ owner, key, answers: prev.owner === owner ? prev.answers : null, failed: true }));
			});
		return () => { active = false; };
	}, [key, id, owner]);

	const reload = useCallback(() => setNonce(n => n + 1), []);
	const same = Boolean(owner) && state.owner === owner;
	return {
		answers: same ? state.answers : null,
		failed: same && state.key === key ? state.failed : false,
		reload,
	};
}
